import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { scrollToId } from '../utils/scroll';

const HowItWorks = () => {
  const navigate = useNavigate();

  const steps = [
    {
      id: 1,
      title: "Browse Our Collection",
      description: "Explore a curated selection of short, high-quality animated videos tailored for your needs.",
      linkLabel: 'Browse videos',
      action: () => scrollToId('videos'),
      icon: (
        <svg
          className="w-12 h-12 text-indigo-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      ),
    },
    {
      id: 2,
      title: "Purchase Your Video",
      description: "Select your favorite animation and complete a secure purchase in just a few clicks.",
      linkLabel: 'Start purchasing',
      action: () => scrollToId('videos'),
      icon: (
        <svg
          className="w-12 h-12 text-indigo-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      ),
    },
    {
      id: 3,
      title: "Download & Use",
      description: "Instantly download your video and start using it for marketing, social media, or presentations.",
      linkLabel: 'View your cart',
      action: () => navigate('/cart'),
      icon: (
        <svg
          className="w-12 h-12 text-indigo-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
          />
        </svg>
      ),
    },
  ];

  return (
    <section id="how-it-works" className="relative bg-gradient-to-b from-white to-gray-50 scroll-mt-20">
      <div className="container mx-auto px-6 lg:px-8 py-20">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <span className="inline-block bg-indigo-100 text-indigo-800 text-sm font-semibold px-4 py-1 rounded-full mb-4">
            Simple Process
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            How to Get Your <span className="text-indigo-600">Perfect Animation</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Get your perfect animated video in three simple steps. Start creating impactful content today!
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={step.id}
              role="button"
              tabIndex={0}
              onClick={step.action}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  step.action();
                }
              }}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: index * 0.15, ease: 'easeOut' }}
              className="relative bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-indigo-100 group cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500"
            >
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gradient-to-br from-indigo-600 to-purple-600 text-white text-lg font-bold w-12 h-12 rounded-full flex items-center justify-center shadow-lg">
                {index + 1}
              </div>

              <div className="flex justify-center mb-6 mt-6">
                <div className="p-4 bg-indigo-50 rounded-full group-hover:bg-indigo-100 transition-colors duration-300">
                  {step.icon}
                </div>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                {step.title}
              </h3>

              <p className="text-gray-600 text-base text-center mb-6">
                {step.description}
              </p>

              <div className="flex items-center justify-center text-indigo-600 font-medium">
                {step.linkLabel}
                <svg
                  className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-16">
          <button
            type="button"
            onClick={() => scrollToId('plans')}
            className="inline-flex items-center px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500"
          >
            Get Started Now
            <svg className="ml-2 -mr-1 w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;